import React, { useCallback, useEffect, useState } from "react";
import { Alert, Box, Button, IconButton, Stack, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { getBackgroundImages, createBackgroundImage, deleteBackgroundImage } from "../../api/adminService";

const PASTE_TYPES = ["image/jpeg", "image/png", "image/webp"];

export default function BackgroundImages() {
  const [images, setImages] = useState([]);
  const [error, setError] = useState("");
  const [isUploading, setIsUploading] = useState(false);

  async function reload() {
    const data = await getBackgroundImages();
    setImages(data);
  }

  useEffect(() => {
    reload().catch(() => setError("Could not load background images. Is the API running?"));
  }, []);

  const uploadFile = useCallback(async (file) => {
    setIsUploading(true);
    setError("");
    try {
      await createBackgroundImage(file);
      await reload();
    } catch (err) {
      setError(err.response?.data || "Could not upload this image.");
    } finally {
      setIsUploading(false);
    }
  }, []);

  async function handleUpload(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    await uploadFile(file);
  }

  // Paste an image straight from the clipboard (Ctrl+V).
  useEffect(() => {
    function handlePaste(e) {
      if (isUploading) return;
      const items = Array.from(e.clipboardData?.items || []);
      const item = items.find((i) => i.kind === "file" && PASTE_TYPES.includes(i.type));
      if (!item) return;
      const blob = item.getAsFile();
      if (!blob) return;
      e.preventDefault();
      const ext = blob.type.split("/")[1];
      const file = new File([blob], blob.name && blob.name !== "image.png" ? blob.name : `pasted-${Date.now()}.${ext}`, {
        type: blob.type,
      });
      uploadFile(file);
    }
    document.addEventListener("paste", handlePaste);
    return () => document.removeEventListener("paste", handlePaste);
  }, [isUploading, uploadFile]);

  async function handleDelete(image) {
    if (!window.confirm("Delete this background image? This cannot be undone.")) return;
    try {
      await deleteBackgroundImage(image.backgroundProductImageId);
      await reload();
    } catch (err) {
      setError(err.response?.data || "This image could not be deleted.");
    }
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h4">Background Images</Typography>
        <Button component="label" variant="contained" startIcon={<AddIcon />} disabled={isUploading}>
          {isUploading ? "Uploading…" : "Upload Image"}
          <input type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={handleUpload} />
        </Button>
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Business owners can pick these photos as backdrops when placing their product photos in the mobile
        app. You can upload a file, or copy an image and paste it here with Ctrl+V.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {typeof error === "string" ? error : "Something went wrong."}
        </Alert>
      )}

      {images.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
          No background images yet.
        </Typography>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
            gap: 2,
          }}
        >
          {images.map((image) => (
            <Box
              key={image.backgroundProductImageId}
              sx={{
                position: "relative",
                borderRadius: 1,
                overflow: "hidden",
                bgcolor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
                aspectRatio: "1 / 1",
              }}
            >
              <Box
                component="img"
                src={image.imageUrl}
                alt={image.name || "Background"}
                sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
              />
              <IconButton
                size="small"
                onClick={() => handleDelete(image)}
                sx={{
                  position: "absolute",
                  top: 4,
                  right: 4,
                  bgcolor: "rgba(0,0,0,0.55)",
                  color: "#fff",
                  "&:hover": { bgcolor: "rgba(211,47,47,0.85)" },
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
